import FormattedMessage from 'I18n/components/FormattedMessage';
import { H2 } from 'foundations-components/transitional/components/H2';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'quartz-routing/react-router';
import { isFulfilled } from 'marketplace-ui-common/requestStates/RequestStateUtils';
import { Grid, GridItem } from 'foundations-components/transitional/components/Grid';
import { Flex } from 'foundations-components/components/Flex';
import LoadingWrapper from 'marketplace-ui-common/layout/components/LoadingWrapper';
import ReviewRenderer from 'marketplace-ui-details/reviews/ReviewRenderer';
import { EcosystemReviewsList } from 'marketplace-ui-reviews/components/display/EcosystemReviewsList';
import { EcosystemReviewsStats } from 'marketplace-ui-reviews/components/display/EcosystemReviewsStats';
import { useGetPreviousReviews } from 'marketplace-ui-reviews/hooks/useGetPreviousReviews';
import { useReviewability } from 'marketplace-ui-reviews/hooks/useReviewability';
import { getAppContextCoreType, getIsPublicApp } from '../../selectors/app';
import { AssetReviewWizard } from './AssetReviewWizard';
import ReviewsListEmptyState from './ReviewsListEmptyState';
import WriteReviewButton from './WriteReviewButton';
import { useReviews } from 'marketplace-ui-reviews/hooks/useReviews';
import ReviewFilters from 'marketplace-ui-reviews/records/ReviewFilters';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export default function Reviews({
  name,
  exampleUrl,
  isPreview,
  offeringId,
  offeringPortalId,
  offeringType,
  onReviewsFulfilled,
  onTrackInteraction,
  providerName,
  reviewType,
  supportUrl,
  thumbnailUrl,
  versionNumber
}) {
  const appContext = useSelector(getAppContextCoreType);
  const {
    pathname
  } = useLocation();
  const isReviewLink = pathname.includes('/write-review');
  const [wizardOpen, setWizardOpen] = useState(isReviewLink);
  const isPublicApp = useSelector(getIsPublicApp);
  const [reviewSubmitted, setReviewSubmitted] = useState(false);
  const {
    reviews: editableReviews
  } = useGetPreviousReviews({
    appContext,
    entityId: offeringId,
    reviewType
  });
  const editedReview = editableReviews[0];
  const {
    unreviewableReason,
    requestState: reviewabilityRequestStatus
  } = useReviewability({
    editPending: !!editedReview && reviewSubmitted,
    entityId: offeringId,
    entityPortalId: offeringPortalId,
    isPublicApp,
    reviewPending: !editedReview && reviewSubmitted,
    reviewType
  });
  const {
    reviewsFetchStatus,
    reviewStatsFetchStatus,
    reviewStats: {
      count: reviewsCount
    } = {
      count: 0
    }
  } = useReviews({
    appContext,
    reviewFilters: new ReviewFilters({
      entityId: offeringId,
      reviewTypes: [reviewType]
    }),
    offeringId,
    reviewType
  });
  const trackInteraction = useCallback((action, eventProperties = {}) => {
    onTrackInteraction(Object.assign({
      action
    }, eventProperties, {
      section: 'review'
    }));
  }, [onTrackInteraction]);
  useEffect(() => {
    if (isFulfilled(reviewStatsFetchStatus) && isFulfilled(reviewsFetchStatus)) {
      onReviewsFulfilled(true);
    }
  }, [reviewStatsFetchStatus, reviewsFetchStatus, onReviewsFulfilled]);
  const renderedWizard = useMemo(() => {
    return /*#__PURE__*/_jsx(LoadingWrapper, {
      requestStatus: reviewabilityRequestStatus,
      children: /*#__PURE__*/_jsx(AssetReviewWizard, {
        editedReview: editedReview,
        name: name,
        thumbnailUrl: thumbnailUrl,
        offeringId: offeringId,
        offeringType: offeringType,
        onClose: () => setWizardOpen(false),
        onSuccess: () => {
          setReviewSubmitted(true);
          setWizardOpen(false);
        },
        onTrackInteraction: trackInteraction,
        providerName: providerName,
        reviewType: reviewType,
        supportLink: supportUrl,
        slug: exampleUrl,
        unreviewableReason: unreviewableReason,
        versionNumber: versionNumber
      })
    });
  }, [exampleUrl, name, offeringId, offeringType, providerName, reviewabilityRequestStatus, supportUrl, thumbnailUrl, trackInteraction, unreviewableReason, editedReview, versionNumber, reviewType]);
  return /*#__PURE__*/_jsxs("div", {
    style: {
      position: 'relative'
    },
    children: [/*#__PURE__*/_jsx(H2, {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "marketplace-assets-ui.themes.details.reviews.title",
        options: {
          name
        }
      })
    }), /*#__PURE__*/_jsxs(Grid, {
      marginBlockStart: "800",
      children: [!!reviewsCount && reviewsCount > 2 && /*#__PURE__*/_jsx(GridItem, {
        size: {
          xs: 12,
          lg: 4
        },
        children: /*#__PURE__*/_jsx(EcosystemReviewsStats, {
          appContext: isPublicApp ? 'public' : 'app',
          flexDirection: "column",
          marginBlock: "600",
          offeringId: offeringId,
          ratingKey: "OVERALL",
          reviewType: reviewType,
          justifyContent: "space-between"
        })
      }), /*#__PURE__*/_jsxs(GridItem, {
        size: {
          xs: 12,
          lg: 8
        },
        children: [!!reviewsCount && /*#__PURE__*/_jsx(Flex, {
          marginBlock: "400",
          justifyContent: "end",
          children: /*#__PURE__*/_jsx(WriteReviewButton, {
            disabled: !!isPreview,
            hasEditableReview: !isPublicApp && !!editedReview,
            onClick: () => setWizardOpen(true),
            reviewPending: reviewSubmitted,
            unreviewableReason: unreviewableReason
          })
        }), /*#__PURE__*/_jsx(EcosystemReviewsList, {
          appContext: isPublicApp ? 'public' : 'app',
          expandable: false,
          offeringId: offeringId,
          renderedEmptyState: /*#__PURE__*/_jsx(ReviewsListEmptyState, {
            reviewPending: reviewSubmitted,
            isPreview: !!isPreview,
            onWriteReviewClick: () => setWizardOpen(true),
            unreviewableReason: unreviewableReason
          }),
          ReviewRenderer: ReviewRenderer,
          reviewType: reviewType,
          searchable: true
        })]
      })]
    }), wizardOpen && renderedWizard]
  });
}